import { Link } from "react-router";
import { Tooltip } from "@mui/material";
import { LinkIcon } from "@ui-icons";
import type { AiContextItem } from "../../AiToolboxSection.context";
import AiAssistantExpandableTree from "../AiAssistantExpandableTree/AiAssistantExpandableTree";
import AiContextBadge from "../AiContextBadges/components/AiContextBadge/AiContextBadge";
import type { ContextEntry } from "./AiAssistantChatContext.utils";

interface AiAssistantChatContextProps {
  entries: ContextEntry[];
  extraContextItems?: AiContextItem[];
  removeContextItem?: (id: string) => void;
  url?: string;
}

/**
 * Displays chat context items (page, env, pipeline, time window, etc.)
 * as a flat tree so the user sees what context will be sent with their message.
 */
const AiAssistantChatContext = ({
  entries,
  extraContextItems,
  removeContextItem,
  url,
}: AiAssistantChatContextProps) => {
  if (!entries?.length && !extraContextItems?.length) return null;

  return (
    <AiAssistantExpandableTree
      title={
        <>
          <span>
            Chat context · {entries?.length + (extraContextItems?.length ?? 0)} items
          </span>
          <Tooltip
            arrow
            disableInteractive
            title="Go to the page where this chat context was generated"
          >
            <Link to={url ?? ""} className="ml-auto">
              <LinkIcon fontSize="small" />
            </Link>
          </Tooltip>
        </>
      }
      data-test-id="ai-assistant-chat-context"
    >
      {entries?.map(({ key, label, value }) => (
        <AiAssistantExpandableTree.Item key={key} title={value}>
          <div className="flex items-center gap-1">
            <span className="shrink-0 text-text-secondary">{label}:</span>
            <span className="truncate">{value}</span>
          </div>
        </AiAssistantExpandableTree.Item>
      ))}
      {extraContextItems?.map((item) => (
        <AiAssistantExpandableTree.Item key={item.id}>
          <AiContextBadge
            item={item}
            onRemove={() => removeContextItem?.(item.id)}
          />
        </AiAssistantExpandableTree.Item>
      ))}
    </AiAssistantExpandableTree>
  );
};

export default AiAssistantChatContext;
